// v1.28.18 — Mobile-only 4-tab bottom navigation for Super User.
// Groups every SU-reachable page into Home / Administration / Reports /
// Settings so a phone user can jump anywhere in ≤ 2 taps without
// hunting through the desktop top-nav. Bottom sheet drawer opens on
// tap for the 3 non-Home tabs; Home routes directly to the SU
// dashboard.
import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import InlineShortcutAssigner from "@/components/InlineShortcutAssigner";
import { useAuth } from "@/context/AuthContext";
import {
    House, Gear, ChartBar, ShieldCheck, X,
    UsersThree, UserPlus, Buildings, Database, CloudArrowUp, PaperPlaneTilt,
    FileText, BookOpen, CalendarBlank, Clock, ChartLineUp, ChartPie, Trophy,
    Table as TableIcon, Broom, Warning, Queue, Pulse, ClockCounterClockwise,
    Bell, EnvelopeSimple, Tag, CreditCard, ChatText, QrCode, Bank, Keyboard, Scales,
    Swap, ArrowsMerge, MagnifyingGlass,
} from "@phosphor-icons/react";

const GROUPS = [
    {
        key: "home",
        label: "Home",
        icon: House,
        direct: "/admin/super/dashboard",
    },
    {
        key: "administration",
        label: "Administration",
        icon: ShieldCheck,
        items: [
            { to: "/admin/super/companies",              label: "Super User: Companies",     icon: Buildings,      testId: "su-nav-companies" },
            { to: "/admin/settings/role-permissions",    label: "Role Permissions",          icon: ShieldCheck,    testId: "su-nav-role-permissions" },
            { to: "/admin/parties?add=1",                label: "Add Party",                 icon: UserPlus,       testId: "su-nav-add-party" },
            { to: "/admin/parties",                      label: "Manage Parties",            icon: UsersThree,     testId: "su-nav-manage-parties" },
            { to: "/admin/parties",                      label: "Party Master",              icon: UsersThree,     testId: "su-nav-party-master" },
            { to: "/admin/parties?resend=1",             label: "Send Portal Link Again",    icon: PaperPlaneTilt, testId: "su-nav-resend-portal" },
            { to: "/admin/parties/merge",                label: "Merge Parties",             icon: ArrowsMerge,    testId: "su-nav-merge-parties" },
            { to: "/admin/parties/transfer-balance",     label: "Transfer Balance",          icon: Swap,           testId: "su-nav-transfer-balance" },
            { to: "/admin/parties/split",                label: "Split Party Period",        icon: ArrowsMerge,    testId: "su-nav-split-party" },
            { to: "/admin/broadcast",                    label: "Broadcast Message",         icon: Bell,           testId: "su-nav-broadcast" },
            { to: "/admin/users",                        label: "Users (Staff)",             icon: UsersThree,     testId: "su-nav-users" },
            { to: "/admin/settings/admins",              label: "Admin Accounts",            icon: ShieldCheck,    testId: "su-nav-admins" },
            { to: "/admin/migration",                    label: "Data Migration",            icon: Database,       testId: "su-nav-migration" },
            { to: "/admin/backups",                      label: "Automatic Backups",         icon: ShieldCheck,    testId: "su-nav-backups" },
            { to: "/admin/super/backup-center",          label: "Backup Center",             icon: ShieldCheck,    testId: "su-nav-backup-center" },
            { to: "/admin/super/daily-archive",          label: "Daily Archive",             icon: CloudArrowUp,   testId: "su-nav-daily-archive" },
            { to: "/admin/super/transfer-party",         label: "Transfer Party",            icon: Swap,           testId: "su-nav-transfer-party" },
            { to: "/admin/super/trace-utr",              label: "Trace UTR",                 icon: MagnifyingGlass, testId: "su-nav-trace-utr" },
            { to: "/admin/release-notes",                label: "Release Notes",             icon: Bell,           testId: "su-nav-release-notes" },
        ],
    },
    {
        key: "reports",
        label: "Reports",
        icon: ChartBar,
        items: [
            { to: "/admin/reports",                      label: "Account Statement",         icon: FileText,           testId: "su-nav-account-statement" },
            { to: "/admin/reports?segment=inactive",     label: "Inactive Parties",          icon: UsersThree,         testId: "su-nav-inactive-parties" },
            { to: "/admin/reports/top-parties",          label: "Top Parties of the Month",  icon: Trophy,             testId: "su-nav-top-parties" },
            { to: "/admin/reports/leaderboard",          label: "Highest Earning Parties",   icon: Trophy,             testId: "su-nav-leaderboard" },
            { to: "/admin/reports/reconcile-grid",       label: "Reconcile Grid",            icon: TableIcon,          testId: "su-nav-reconcile-grid" },
            { to: "/admin/reports/outstanding",          label: "Outstanding Report",        icon: ChartBar,           testId: "su-nav-outstanding" },
            { to: "/admin/reports/opening-closing",      label: "Opening & Closing Balance", icon: Scales,             testId: "su-nav-opening-closing" },
            { to: "/admin/reports/day-book",             label: "Day Book",                  icon: BookOpen,           testId: "su-nav-day-book" },
            { to: "/admin/monthly-summary",              label: "Monthly Summary",           icon: CalendarBlank,      testId: "su-nav-monthly-summary" },
            { to: "/admin/aging",                        label: "Aging Report",              icon: Clock,              testId: "su-nav-aging" },
            { to: "/admin/payments-by-upi",              label: "Payments by UPI",           icon: ChartBar,           testId: "su-nav-payments-by-upi" },
            { to: "/admin/reports/commission",           label: "Commission Report",         icon: ChartLineUp,        testId: "su-nav-commission" },
            { to: "/admin/reports/payment-modes",        label: "Payment Modes Report",      icon: ChartPie,           testId: "su-nav-payment-modes-report" },
            { to: "/admin/reports/payment-calendar",     label: "Payment Calendar",          icon: CalendarBlank,      testId: "su-nav-payment-calendar" },
            { to: "/admin/reports/verification-queue",   label: "Verification Queue",        icon: Queue,              testId: "su-nav-verification-queue" },
            { to: "/admin/reports/suspense",             label: "Suspense Entries",          icon: Warning,            testId: "su-nav-suspense" },
            { to: "/admin/reports/stranded",             label: "Stranded Commissions",      icon: Broom,              testId: "su-nav-stranded" },
            { to: "/admin/reports/duplicate-utrs",       label: "Duplicate UTRs",            icon: Warning,            testId: "su-nav-duplicate-utrs" },
            { to: "/admin/reports/invalid-utrs",         label: "Invalid UTR Records",       icon: Warning,            testId: "su-nav-invalid-utrs" },
            { to: "/admin/reports/mediator-commission",  label: "Mediator Commission",       icon: CreditCard,         testId: "su-nav-mediator-commission" },
            { to: "/admin/activity",                     label: "Activity Report",           icon: Pulse,              testId: "su-nav-activity" },
            { to: "/admin/reports/user-activity",        label: "User Activity Log",         icon: Pulse,              testId: "su-nav-user-activity" },
            { to: "/admin/login-history",                label: "Login History",             icon: ClockCounterClockwise, testId: "su-nav-login-history" },
            { to: "/admin/notifications",                label: "Notifications",             icon: Bell,               testId: "su-nav-notifications" },
            { to: "/admin/email-log",                    label: "Email Log",                 icon: EnvelopeSimple,     testId: "su-nav-email-log" },
        ],
    },
    {
        key: "settings",
        label: "Settings",
        icon: Gear,
        items: [
            { to: "/admin/company-upis",                 label: "Company UPI IDs",           icon: QrCode,       testId: "su-nav-company-upis" },
            { to: "/admin/upi-mappings",                 label: "UPI Account Mapping",       icon: Bank,         testId: "su-nav-upi-mappings" },
            { to: "/admin/settings/payment-modes",       label: "Payment Modes",             icon: CreditCard,   testId: "su-nav-payment-modes" },
            { to: "/admin/settings/remark-presets",      label: "Remark Presets",            icon: ChatText,     testId: "su-nav-remark-presets" },
            { to: "/admin/settings/shortcuts",           label: "Keyboard Shortcuts",        icon: Keyboard,     testId: "su-nav-shortcuts" },
            { to: "/admin/pricing",                      label: "Pricing Rules",             icon: Tag,          testId: "su-nav-pricing" },
            { to: "/admin/mediators",                    label: "Mediators",                 icon: CreditCard,   testId: "su-nav-mediators" },
            { to: "/admin/portals",                      label: "Portals",                   icon: Buildings,    testId: "su-nav-portals" },
        ],
    },
];

export default function SuperUserBottomNav() {
    const [openKey, setOpenKey] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isSuperUser = !!user?.is_super_user;

    const pendingNavRef = useRef(null);
    const pendingOpenRef = useRef(null);

    const active = GROUPS.find((g) => {
        if (g.key === "home") return location.pathname === "/admin" || location.pathname.startsWith("/admin/super/dashboard");
        if (!g.items) return false;
        return g.items.some((it) => location.pathname === it.to.split("?")[0]);
    });

    // Force-clear Radix's body scroll lock + any leftover overlay element on
    // every menu close AND on every route change. Radix normally cleans up
    // itself when the close animation ends, but if the animation is
    // interrupted (e.g. user taps another tab quickly, or a navigation
    // preempts the transition) the body-lock styles and the fading overlay
    // can stay stuck for ~300 ms — that's the "fog" complaint. We wipe both
    // eagerly.
    //
    // IMPORTANT: Radix's Dialog Overlay renders as
    //   `<div class="fixed inset-0 z-50 bg-black/80 ..." data-state="open|closed">`
    // portalled directly to <body>. It does NOT get `data-radix-portal` or
    // `data-radix-dialog-overlay` — those attribute names don't exist. And
    // because we fire this cleanup synchronously right after `setOpenKey(null)`,
    // React hasn't flushed Radix's state flip yet, so `[data-state="closed"]`
    // still matches nothing at that instant. We therefore identify the
    // overlay by its className signature via JS (attribute-substring selectors
    // with `/` inside are also flaky across browsers), and we re-run the sweep
    // across a few animation frames to catch stragglers.
    const nukeOverlays = () => {
        // If a sheet is still meant to be open, don't nuke — the overlay is
        // legitimate. `openKey` is captured via closure at call time.
        if (openKey) return;
        try {
            // v1.29.02 — Match the overlay by CSS-class signature ONLY.
            // Previously we required `data-state` on the element, but Radix
            // portals the overlay before setting `data-state` on the very
            // first frame — during an interrupted close (rapid tap → nav)
            // the attribute momentarily disappears, our filter missed the
            // node, and the black fog stuck for ~300ms. The class combo
            // `fixed inset-0 ... bg-black` from `sheet.jsx` line 19 is
            // unique to Radix Dialog/Sheet overlays in this app — nothing
            // else at body-level renders a full-screen black-tint div —
            // so it's safe to key on that alone.
            //
            // Belt-and-braces: first neutralise it (pointer-events:none +
            // opacity:0) so the underlying UI is tappable INSTANTLY. Then
            // remove the node on the next frame so Radix's own animationend
            // cleanup doesn't fight ours.
            const nodes = document.querySelectorAll('div.fixed.inset-0');
            nodes.forEach((el) => {
                const cls = typeof el.className === 'string' ? el.className : '';
                if (cls.includes('bg-black') && cls.includes('z-50')) {
                    el.style.pointerEvents = 'none';
                    el.style.opacity = '0';
                    el.style.transition = 'none';
                    // v1.29.70 — DO NOT el.remove() this node. It is a Radix-
                    // portalled overlay that React still owns; removing it out
                    // from under React threw a removeChild NotFoundError on the
                    // Administration sheet close → BootErrorBoundary hard-reload
                    // → super-user got logged out. Neutralising it above
                    // (invisible + non-blocking) already unblocks the UI; Radix
                    // removes the node itself when its exit animation ends.
                }
            });
        } catch (_e) { /* no-op */ }
    };

    const forceClearBodyLock = () => {
        try {
            document.body.style.pointerEvents = "";
            document.body.style.overflow = "";
            document.body.removeAttribute("data-scroll-locked");
            // Some Radix versions apply the lock via aria-hidden on siblings.
            document.querySelectorAll('body > [aria-hidden="true"][data-aria-hidden]').forEach((el) => el.removeAttribute("aria-hidden"));
        } catch (_e) { /* no-op */ }
        // v1.29.02 — Sample the DOM aggressively during the close animation,
        // not just once it's over. Radix's exit animation runs ~200ms; we
        // poll on frame 1, next rAF, and at 50/120/240/380 ms so no
        // interrupted-close timing hole can leave an overlay stuck.
        nukeOverlays();
        requestAnimationFrame(() => {
            nukeOverlays();
            document.body.style.pointerEvents = "";
            document.body.style.overflow = "";
            document.body.removeAttribute("data-scroll-locked");
        });
        [50, 120, 240, 380].forEach((ms) => setTimeout(nukeOverlays, ms));
    };

    useEffect(() => {
        if (openKey) return;
        forceClearBodyLock();
    }, [openKey, location.pathname]);

    const closeThenNavigate = (to) => {
        pendingNavRef.current = to;
        setOpenKey(null);
        // Fire the cleanup immediately — don't wait for the animation to
        // finish. The Sheet visually slides out; the body underneath is
        // already tappable while it does.
        forceClearBodyLock();
    };

    const onTabClick = (g) => {
        if (g.direct) {
            if (openKey) {
                closeThenNavigate(g.direct);
            } else {
                navigate(g.direct);
            }
            return;
        }
        if (openKey && openKey !== g.key) {
            pendingOpenRef.current = g.key;
            setOpenKey(null);
            forceClearBodyLock();
        } else {
            setOpenKey(openKey === g.key ? null : g.key);
        }
    };

    // Only fires for the CLOSE animation (openKey is already null by then) —
    // that's the earliest point it's safe to navigate or open the next menu
    // without leaving the overlay/body-lock stuck mid-transition.
    const handleSheetAnimationEnd = (e) => {
        if (e.target !== e.currentTarget || openKey) return;
        if (pendingNavRef.current) {
            const to = pendingNavRef.current;
            pendingNavRef.current = null;
            navigate(to);
        } else if (pendingOpenRef.current) {
            const next = pendingOpenRef.current;
            pendingOpenRef.current = null;
            setOpenKey(next);
        }
    };

    const openGroup = openKey ? GROUPS.find((g) => g.key === openKey) : null;

    return (
        <>
            {/* Fixed bottom tab bar — mobile only */}
            <nav
                data-testid="su-bottom-nav"
                className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)] md:hidden"
            >
                <div className="mx-auto grid max-w-md grid-cols-4">
                    {GROUPS.map((g) => {
                        const Icon = g.icon;
                        const isActive = active?.key === g.key || openKey === g.key;
                        return (
                            <button
                                key={g.key}
                                type="button"
                                data-testid={`su-bottom-tab-${g.key}`}
                                aria-current={isActive ? "page" : undefined}
                                onClick={() => onTabClick(g)}
                                className={`flex flex-col items-center gap-0.5 py-2 text-[10px] font-semibold transition-colors ${
                                    isActive ? "text-amber-700" : "text-slate-500 hover:text-slate-800"
                                }`}
                            >
                                <Icon size={20} weight={isActive ? "fill" : "regular"} />
                                <span>{g.label}</span>
                            </button>
                        );
                    })}
                </div>
            </nav>

            {/* Sheet drawer with the tab's items */}
            <Sheet open={!!openKey} onOpenChange={(v) => { if (!v) { setOpenKey(null); forceClearBodyLock(); } }}>
                <SheetContent
                    side="bottom"
                    data-testid={`su-sheet-${openKey || "closed"}`}
                    className="max-h-[80vh] overflow-y-auto rounded-t-2xl p-0 !duration-150 data-[state=closed]:!duration-100"
                    onAnimationEnd={handleSheetAnimationEnd}
                    onEscapeKeyDown={forceClearBodyLock}
                    onPointerDownOutside={forceClearBodyLock}
                >
                    <SheetHeader className="border-b border-slate-100 bg-gradient-to-r from-amber-50 to-orange-50 px-5 py-3">
                        <SheetTitle className="flex items-center gap-2 text-left text-base font-black text-slate-900">
                            {openGroup?.icon && React.createElement(openGroup.icon, { size: 18, weight: "fill", className: "text-amber-700" })}
                            {openGroup?.label}
                        </SheetTitle>
                    </SheetHeader>
                    {openGroup?.items && (
                        <ul className="divide-y divide-slate-100 pb-6">
                            {openGroup.items.map((it) => {
                                const ItemIcon = it.icon;
                                return (
                                    <li key={it.testId} className="flex items-center gap-2 pr-3">
                                        <NavLink
                                            to={it.to}
                                            data-testid={it.testId}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                closeThenNavigate(it.to);
                                            }}
                                            className={({ isActive }) =>
                                                `flex flex-1 items-center gap-3 px-5 py-3.5 text-sm text-slate-800 active:bg-slate-100 ${isActive ? "bg-amber-50 font-semibold text-amber-800" : ""}`
                                            }
                                        >
                                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                                                <ItemIcon size={16} weight="bold" />
                                            </span>
                                            <span className="min-w-0 flex-1 truncate">{it.label}</span>
                                        </NavLink>
                                        {isSuperUser && (
                                            <InlineShortcutAssigner
                                                action_id={`nav:${it.testId}`}
                                                label={it.label}
                                                target_route={it.to}
                                                category={openGroup?.label || "Menu"}
                                            />
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </SheetContent>
            </Sheet>
        </>
    );
}
